import { useNavigate } from 'react-router';
import { HStack, Box, Text, VStack } from '@chakra-ui/react';
import type { User } from '@halo/chat-sdk-react';
import { useAppStore } from '@halo/chat-sdk-react';
import { CallIcon, ChatHeaderMediaCall, ChatIcon, GiftIcon, MoreIcon } from '@halo/ui-chat-react';
import Paths from '@/configs/router/paths';
import { useActionsStore } from '@/store/actions';
import { useMediaError } from '@/scripts/hooks';

export default function ReceiverActions({ user }: { user: User }) {
  const { t } = useTranslation();
  const { createSingleSession, mediaCall, mediaActivatedId, hasOtherDeviceJoinCall } =
    useAppStore();
  const { setProfileOpen } = useActionsStore();
  const { mediaError } = useMediaError();
  const navigate = useNavigate();

  const [isClicked, setIsClicked] = useState(false);

  const isDisabled = false;
  const isDisabledCall = useMemo(() => {
    return !!mediaActivatedId || hasOtherDeviceJoinCall;
  }, [mediaActivatedId, hasOtherDeviceJoinCall]);

  const showUserError = !user.have_group_key || !user.have_user_key;

  const navData = [
    {
      key: 'Call',
      icon: <CallIcon />,
      text: t('profile.call'),
      disabled: isDisabledCall,
    },
    {
      key: 'Chat',
      icon: <ChatIcon />,
      text: t('profile.chat'),
      disabled: isDisabled,
      onclick: async () => {
        if (isClicked) return;
        setIsClicked(true);
        const session = await createSingleSession(user.user_id);
        navigate(Paths.MESSAGES_CHATS + '/' + session?.chat_id);
        setProfileOpen(false);
        setIsClicked(false);
      },
    },
    {
      key: 'Gift',
      icon: <GiftIcon />,
      text: t('profile.gift'),
      onclick: () => {},
      disabled: true,
    },
    {
      key: 'More',
      icon: <MoreIcon />,
      text: t('profile.more'),
      onclick: () => {},
      disabled: true,
    },
  ];

  const onMediaClick = useCallback(
    async (key: any) => {
      if (isClicked) return;
      setIsClicked(true);
      const session = await createSingleSession(user.user_id);
      navigate(Paths.MESSAGES_CHATS + '/' + session?.chat_id);
      setProfileOpen(false);
      setIsClicked(false);
      mediaCall({
        session_id: session?.chat_id,
        type: key,
        membersId: [session?.user_info!.user_id],
      }).catch((error) => {
        mediaError(error, 'call');
      });
    },
    [
      isClicked,
      createSingleSession,
      user.user_id,
      navigate,
      setProfileOpen,
      mediaCall,
      mediaError,
    ]
  );

  if (showUserError) {
    return null;
  }

  return (
    <HStack w="full" justifyContent="space-between" gap="2.5">
      {navData
        .filter((item) => !item.disabled)
        .map((item) => {
          const Item = (
            <>
              <Box className="shake">{item.icon}</Box>
              <Text fontSize="md" fontWeight="semibold">
                {item.text}
              </Text>
            </>
          );
          if (item.key === 'Call') {
            return (
              <ChatHeaderMediaCall
                key={item.key}
                isGroup={false}
                onMediaClick={onMediaClick}
                triggerProps={{
                  asChild: true,
                }}
              >
                <VStack
                  bg="bg"
                  flex={1}
                  rounded="xl"
                  py="4"
                  alignItems="center"
                  gap="1"
                  key={item.key}
                  cursor="pointer"
                  className="VStack"
                >
                  {Item}
                </VStack>
              </ChatHeaderMediaCall>
            );
          }
          return (
            <VStack
              bg={item.key === 'Gift' ? '#F0E9FF' : 'bg'}
              flex={1}
              rounded="xl"
              py="4"
              alignItems="center"
              gap="1"
              key={item.key}
              onClick={item?.onclick}
              cursor="pointer"
            >
              {Item}
            </VStack>
          );
        })}
    </HStack>
  );
}
